import styled from 'styled-components';
import { Badge } from '@shared/ui/Badge';
import { Loader } from '@shared/ui/Progress';
import { Table } from '@shared/ui/Table';
import { useListClaimPartiesQuery, useListContractPartiesQuery } from '@shared/api';
import {
  counterpartyStatusLabel,
  counterpartyStatusTone,
} from '@shared/lib/statusLabels';

/**
 * Блок «Упомянутые стороны (N)» в карточке договора/претензии. LLM
 * извлекает все ИНН из документа, backend джоинит их с counterparties и
 * возвращает enriched-список. Каждая строка — ссылка на карточку
 * контрагента ([[CounterpartyCard]]) через переданный callback.
 *
 * WS-событие entity_counterparties_extracted инвалидирует тег Parties,
 * данные подтягиваются автоматически.
 */

const Section = styled.div`
  margin-top: ${({ theme }) => theme.spacing(5)};

  & > h4 {
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const Empty = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  font-style: italic;
`;

const PartyRow = styled.tr`
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast}
    ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }

  &:focus-visible {
    outline: none;
    background: ${({ theme }) => theme.colors.primarySoft};
  }
`;

const Inn = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const PartyName = styled.span<{ $faded?: boolean }>`
  color: ${({ $faded, theme }) => ($faded ? theme.colors.text.muted : theme.colors.text.primary)};
  font-style: ${({ $faded }) => ($faded ? 'italic' : 'normal')};
`;

const CONTRACT_ROLE_LABELS: Record<string, string> = {
  seller: 'Продавец',
  buyer: 'Покупатель',
  customer: 'Заказчик',
  contractor: 'Подрядчик',
  guarantor: 'Поручитель',
  agent: 'Агент',
  other: 'Иное',
};

const CLAIM_ROLE_LABELS: Record<string, string> = {
  plaintiff: 'Истец',
  defendant: 'Ответчик',
  third_party: 'Третье лицо',
  prosecutor: 'Прокурор',
  court: 'Суд',
  other: 'Иное',
};

function roleLabel(scope: 'contract' | 'claim', role: string): string {
  const map = scope === 'contract' ? CONTRACT_ROLE_LABELS : CLAIM_ROLE_LABELS;
  return map[role] ?? role;
}

interface Props {
  scope: 'contract' | 'claim';
  entityId: string;
  /** В разборе ли документ сейчас — определяет текст placeholder'а. */
  isAnalyzing: boolean;
  /** Открыть карточку контрагента поверх. */
  onOpenCounterparty: (counterpartyId: string) => void;
}

export function EntityPartiesSection({
  scope,
  entityId,
  isAnalyzing,
  onOpenCounterparty,
}: Props) {
  // Хуки нельзя вызывать условно — выбираем по scope, скипаем неактуальный.
  const contractQuery = useListContractPartiesQuery(entityId, { skip: scope !== 'contract' });
  const claimQuery = useListClaimPartiesQuery(entityId, { skip: scope !== 'claim' });
  const query = scope === 'contract' ? contractQuery : claimQuery;
  const items = query.data?.items ?? [];

  return (
    <Section>
      <h4>Упомянутые стороны ({items.length})</h4>
      {query.isLoading ? (
        <Loader />
      ) : items.length === 0 ? (
        <Empty>
          {isAnalyzing
            ? 'Контрагенты будут извлечены после анализа.'
            : 'Контрагенты с ИНН не обнаружены.'}
        </Empty>
      ) : (
        <Table>
          <thead>
            <tr>
              <th style={{ width: '18%' }}>ИНН</th>
              <th>Наименование</th>
              <th style={{ width: '18%' }}>Роль</th>
              <th style={{ width: '20%' }}>Статус ЕГРЮЛ</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => {
              const display = p.short_name ?? p.full_name;
              const fallback = !display && p.extracted_inn ? `ИНН ${p.extracted_inn}` : null;
              return (
                <PartyRow
                  key={p.counterparty_id}
                  tabIndex={0}
                  onClick={() => onOpenCounterparty(p.counterparty_id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onOpenCounterparty(p.counterparty_id);
                    }
                  }}
                  title="Открыть карточку контрагента"
                >
                  <td>
                    <Inn>{p.inn}</Inn>
                  </td>
                  <td>
                    {display ? (
                      <PartyName>{display}</PartyName>
                    ) : (
                      <PartyName $faded>{fallback ?? '—'}</PartyName>
                    )}
                  </td>
                  <td>{roleLabel(scope, p.role)}</td>
                  <td>
                    <Badge $tone={counterpartyStatusTone(p.status)}>
                      {counterpartyStatusLabel(p.status)}
                    </Badge>
                  </td>
                </PartyRow>
              );
            })}
          </tbody>
        </Table>
      )}
    </Section>
  );
}
